import React, { useEffect, useRef, useState } from 'react';

/**
 * Single-select, search-that-closes-on-pick workflow picker -- modelled on
 * HierarchyNodeTreeSelect.js, the only existing component with that shape.
 * Replaces SubmitForApprovalModal's old hand-rolled always-expanded radio
 * list, which used none of the `multiselect-*` chrome every other picker in
 * the app shares.
 *
 * Deliberately takes `templates` as a PROP rather than fetching them itself,
 * unlike its sibling pickers: the caller already needs the list in hand to
 * decide auto-selection (single applicable template, or the one last used),
 * and a self-fetching child would issue a second, redundant request for data
 * the parent already has.
 */
function WorkflowTemplateSelect({ templates, value, onChange, disabled,
                                 placeholder = 'Select a workflow...' }) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const ref = useRef(null);

  useEffect(() => {
    if (!open) return undefined;
    const handlePointerDown = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    const handleKeyDown = (e) => {
      if (e.key === 'Escape') setOpen(false);
    };
    document.addEventListener('mousedown', handlePointerDown);
    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('mousedown', handlePointerDown);
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [open]);

  const trimmedQuery = query.trim().toLowerCase();
  const visible = trimmedQuery
    ? templates.filter((t) => t.name.toLowerCase().includes(trimmedQuery))
    : templates;

  const select = (templateId) => {
    onChange(templateId);
    setOpen(false);
    setQuery('');
  };

  const selected = templates.find((t) => String(t.id) === String(value));
  const label = selected ? selected.name : placeholder;

  return (
    <div className="multiselect" ref={ref}>
      <button
        type="button"
        className="multiselect-toggle"
        onClick={() => setOpen((o) => !o)}
        disabled={disabled}
        aria-haspopup="listbox"
        aria-expanded={open}
      >
        <span className="multiselect-toggle-label">{label}</span>
        <span className="multiselect-toggle-caret" aria-hidden="true">▾</span>
      </button>
      {open && (
        <div className="multiselect-panel">
          <input
            type="text"
            className="multiselect-search"
            placeholder="Filter workflows..."
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            autoFocus
          />
          <div className="multiselect-options" role="listbox">
            {visible.map((t) => (
              <label key={t.id} className="multiselect-option">
                <input
                  type="radio"
                  checked={String(value) === String(t.id)}
                  onChange={() => select(t.id)}
                />
                <strong>{t.name}</strong>
                {' -- '}{t.step_count} gate{t.step_count === 1 ? '' : 's'}
                {' '}
                <span style={{ fontSize: '12px', color: 'var(--muted)' }}>
                  ({t.is_local ? 'defined here' : `from ${t.owner_node_name}`})
                  {t.is_last_used && ' · used last time'}
                </span>
              </label>
            ))}
            {visible.length === 0 && (
              <div style={{ fontSize: '13px', color: 'var(--muted)', padding: '6px 8px' }}>
                No matches.
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

export default WorkflowTemplateSelect;
